
































































































































































import { Vue, Component, Watch } from "vue-property-decorator";
import { Month } from "@/models/calendar/month";
import { Day } from "@/models/calendar/day";
import moment from "moment";
import { getModule } from "vuex-module-decorators";
import SnackbarModule from "@/store/snackbarModule";
import Magnifiable from "@/components/magnifiable/Magnifiable.vue";
import YearModule from "@/store/yearModule";
import EventService from "@/services/eventService";
import EventCategoryService from "@/services/eventCategoryService";
import EventModel from "@/models/event/eventModel";
import EventCell from "@/components/calendar/EventCell.vue";
import EventDialog from "@/components/dialogs/EventDialog.vue";
import UserService from "@/services/userService";
import UserModel from "@/models/user/userModel";
import EventCategoryModel from "@/models/event/eventCategoryModel";

const yearModule = getModule(YearModule);
const snackbarModule = getModule(SnackbarModule);

@Component({
  components: {
    EventCell,
    EventDialog,
    Magnifiable
  }
})
export default class Calendar extends Vue {
  private days: Array<string> = [];
  private months: Array<Month> = [];
  private users: Array<UserModel> = [];
  private categories: Array<EventCategoryModel> = [];
  private events: Array<EventModel> = [];
  private event: EventModel = new EventModel();
  private showKey = false;
  private dialog = false;
  private isEdit = false;
  private isMobile = false;
  private zoomLevel = 0.4;
  private originalViewport = '';

  get year(): number {
    return yearModule.getYear;
  }

  get mobileZoomStyle(): any {
    if (!this.isMobile) return {};
    
    return {
      transform: `scale(${this.zoomLevel})`,
      transformOrigin: '0 0',
      width: `${100 / this.zoomLevel}%`,
      height: `${100 / this.zoomLevel}%`
    };
  }


  private handleZoomChange(event: Event) {
    const target = event.target as HTMLInputElement;
    this.zoomLevel = parseInt(target.value) / 100;
  }

  private getUserCountClass(): string {
    const userCount = this.users.length;
    if (userCount >= 1 && userCount <= 3) {
      return "1-3";
    } else if (userCount >= 4 && userCount <= 7) {
      return "4-7";
    } else {
      return "8-plus";
    }
  }

  private async created() {
    try {
      await this.getUsers();
      await this.getEventCategories();
      await this.calculateCalendar();
      await this.getEventCategories();
    } catch {
      snackbarModule.snackError("Something went wrong, please try again");
    }
  }

  private mounted() {
    this.detectMobile();
    if (this.isMobile) {
      this.setupMobileZoom();
    }
    
    // Listen for orientation changes
    window.addEventListener('orientationchange', this.handleOrientationChange);
    window.addEventListener('resize', this.detectMobile);
  }
  
  private beforeDestroy() {
    if (this.isMobile) {
      this.restoreViewport();
    }
    window.removeEventListener('orientationchange', this.handleOrientationChange);
    window.removeEventListener('resize', this.detectMobile);
  }

  private detectMobile() {
    this.isMobile = window.innerWidth <= 768 || /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  }

  private setupMobileZoom() {
    // Store original viewport
    const viewportMeta = document.querySelector('meta[name="viewport"]') as HTMLMetaElement;
    if (viewportMeta) {
      this.originalViewport = viewportMeta.content;
      // Update viewport to allow more flexibility
      viewportMeta.content = 'width=device-width, initial-scale=1.0, minimum-scale=0.1, maximum-scale=3.0, user-scalable=yes';
    }
  }

  private restoreViewport() {
    if (this.originalViewport) {
      const viewportMeta = document.querySelector('meta[name="viewport"]') as HTMLMetaElement;
      if (viewportMeta) {
        viewportMeta.content = this.originalViewport;
      }
    }
  }

  private handleOrientationChange() {
    setTimeout(() => {
      this.detectMobile();
    }, 500);
  }

  private zoomIn() {
    if (this.zoomLevel < 1.0) {
      this.zoomLevel = Math.min(1.0, this.zoomLevel + 0.1);
    }
  }

  private zoomOut() {
    if (this.zoomLevel > 0.2) {
      this.zoomLevel = Math.max(0.2, this.zoomLevel - 0.1);
    }
  }

  private resetZoom() {
    this.zoomLevel = 0.4;
  }
  
  private async getEventCategories() {
    try {
      this.categories = await EventCategoryService.getAllEventCategorysByCalendarAsync(
        this.$route.params.id
      );
    } catch {
      snackbarModule.snackError("Failed to fetch event categories");
    }
  }

  private handleCellClick(day: Day, month: Month, user: UserModel) {    
    const existingEvent = this.eventMatches(user.calendarUserId, day.date, month.name);
    
    // Only call openOnDay if there's no existing event
    if (!existingEvent) {
      this.openOnDay(day, month, user);
    }
  }

  private editEvent(event: EventModel) {
    this.dialog = this.isEdit = true;
    this.event = JSON.parse(JSON.stringify(event));
  }

  private async getUsers() {
    try {
      this.users = await UserService.getAllUserSettings(this.$route.params.id);
      if (this.users.length == 0) {
        alert("No users selected, please configure Calendar Settings");
        this.$router.push({ name: "User Settings" });
      }
    } catch {
      snackbarModule.snackError("Failed to fetch user settings");
    }
  }

  private async getEvents() {
    try {
      this.events = await EventService.getAllEventsByYear(
        yearModule.getYear,
        this.$route.params.id
      );
      this.events.forEach(event => {
        event.eventUserIds = event.eventUsers.map(x => x.userId);
      });
    } catch {
      snackbarModule.snackError("Failed to fetch Events");
    }
  }

  private async calculateCalendar() {
    this.days = [];
    this.months = [];

    await this.getEvents();
    this.populateWeekdays();
    this.populateCalendarByYear();
  }

  private populateWeekdays() {
    for (let i = 0; i < 38; i++) {
      const dayIndex = i % moment.weekdaysShort().length;
      this.days.push(moment.weekdaysShort()[dayIndex]);
    }
  }

  private populateCalendarByYear() {
    for (const monthName of moment.monthsShort()) {
      const days = [];

      for (
        let i = 0;
        i <
        moment()
          .year(this.year)
          .month(monthName)
          .daysInMonth();
        i++
      ) {
        days.push({
          date: i + 1,
          day: moment()
            .year(this.year)
            .month(monthName)
            .date(i + 1)
            .format("ddd")
        });
      }

      const month = {
        name: monthName,
        days: days
      };

      this.prependBlankDays(month);
      this.appendBlankDays(month);
      this.months.push(month);
    }
  }

  private prependBlankDays(month: Month) {
    const blanks: Array<Day> = [];
    const firstDay = month.days[0].day;

    for (const day of this.days) {
      if (day === firstDay) {
        break;
      }

      blanks.push({
        day: day,
        date: -1
      });
    }

    month.days = blanks.concat(month.days);
  }

  private appendBlankDays(month: Month) {
    for (let i = month.days.length; i < this.days.length; i++) {
      month.days.push({
        day: this.days[i],
        date: -1
      });
    }
  }

  private openOnDay(day: Day, month: Month, user: UserModel) {    
    if (!this.event.eventUserIds.includes(user.calendarUserId)) {
      this.event.eventUserIds.push(user.calendarUserId);
    }
    this.event.startDate = moment()
      .year(this.year)
      .month(month.name)
      .date(day.date)
      .format("YYYY-MM-DD");
    this.event.endDate = moment()
      .year(this.year)
      .month(month.name)
      .date(day.date)
      .format("YYYY-MM-DD");
    this.$root.$emit("openNavbarModal");
  }

  private eventMatches(
    userId: string,
    date: number,
    month: string
  ): void | EventModel {
    if (date === -1) {
      return;
    }

    const dateMoment = moment()
      .year(this.year)
      .month(month)
      .date(date);

    const matchedEvent = this.events.find(x => {
      const userMatches = x.eventUserIds.includes(userId);
      const startMonth = moment(x.startDate).format("MMM");

      if (startMonth === month) {
        return userMatches && dateMoment.isSame(x.startDate, "day");
      } else if (startMonth !== month && date === 1) {
        return (
          userMatches &&
          dateMoment.isBetween(x.startDate, x.endDate, undefined, "[]")
        );
      }
    });

    if (matchedEvent) {
      return matchedEvent;
    }
  }

  @Watch("year")
  private async onYearChanged() {
    await this.calculateCalendar();
  }
}
